import type { PhoneCountry } from "./types";

export const getPhoneCountriesFromPicklist = (
  value: unknown
): PhoneCountry[] => {
  if (!Array.isArray(value)) return [];
  return value as PhoneCountry[];
};

/**
 * The countries that publish landline rules, each with its mobile `min`/`max`/`prefixes`
 * replaced by the landline ones — so the result drops straight into the same validators
 * (`getPhoneRegexByCountry`, `isValidPhoneNumberByCountryCode`, ...) as the mobile list.
 */
export const getLandlineCountriesFromPicklist = (
  value: unknown
): PhoneCountry[] =>
  getPhoneCountriesFromPicklist(value)
    .filter((country) => country?.landline)
    .map(({ landline, ...country }) => ({
      ...country,
      min: landline!.min,
      max: landline!.max,
      prefixes: landline!.prefixes || [],
    }));

/** Whether the business lets cashiers enter landline numbers (shows the Mobile/Landline picker). */
export const selectallowLandlineForCountry = (state: {
  businessStore?: { businessData?: { allowLandlineForCountry?: boolean } };
}): boolean => Boolean(state?.businessStore?.businessData?.allowLandlineForCountry);

export const findPhoneCountry = (
  countries: PhoneCountry[] = [],
  countryCode?: string
): PhoneCountry | null => {
  if (!countryCode || !countries?.length) return null;

  return (
    countries.find(
      (country) => country?.code === countryCode
    ) || null
  );
};

export const getSelectedPhoneCountry = (
  countries: PhoneCountry[] = [],
  countryCode?: string
): PhoneCountry | null => {
  return findPhoneCountry(countries, countryCode);
};

export const getPhoneCountryMeta = (
  selectedPhoneCountry: PhoneCountry | null
) => ({
  phoneCountryCode: selectedPhoneCountry?.code,
  phoneCountryIsoCode: selectedPhoneCountry?.code,
  phoneCountryPrefix: selectedPhoneCountry?.prefix,
  min: Number(selectedPhoneCountry?.min) || 10,
  max: Number(selectedPhoneCountry?.max) || 10,
});
/**
 * Which phone country a picker starts on (`usePhoneCountrySelection`): the customer's own country
 * when a caller passes one (mobile's customer form and prepaid flow do; matched ignoring case), else
 * the one already picked if the list still has it, else the merchant's, else the first. Web's rule,
 * which supersedes mobile's (2026-09-29): the merchant's code matches exactly and there is always a
 * first-country fallback. `null` only for an empty list.
 */
export const pickPhoneCountry = (
  countries: PhoneCountry[],
  { previous, merchantCode, customerCode }: { previous?: PhoneCountry | null; merchantCode?: string; customerCode?: string } = {}
): PhoneCountry | null => {
  if (!countries?.length) return null;
  const customer = customerCode ? countries.find((c) => c.code?.toUpperCase() === customerCode.toUpperCase()) : undefined;
  if (customer) return customer;
  const kept = previous?.code ? countries.find((c) => c.code === previous.code) : undefined;
  if (kept) return kept;
  return countries.find((c) => c.code === merchantCode) || countries[0];
};
